import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { connect } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';

import { getUserByEmail, getUserByUsername, registerUser } from '../../shared/requests/UserRequests';
import {
  validateFirstname,
  validateLastname,
  validateEmail,
  validateUsername,
  validatePassword,
} from '../../shared/validators/RegisterValidator';
import { RECAPTCHA_SITEKEY } from '../../shared/Constants';
import logo from '../../static/img/logo.webp';
import errorIcon from '../../static/img/icons/warning_icon.svg';
import successIcon from '../../static/img/icons/checked_icon.svg';

const EMAIL_ERROR_TEXT = 'You already have an account!';

const styles = theme => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    marginBottom: theme.spacing(),
  },
  formControl: {
    margin: theme.spacing(),
  },
  button: {
    marginTop: theme.spacing(3),
  },
  progress: {
    marginTop: theme.spacing(2),
  },
  logo: {
    height: 60,
    marginBottom: theme.spacing(3),
  },
  messageContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  icon: {
    height: 40,
    width: 40,
    marginBottom: theme.spacing(2),
  },
  recaptcha: {
    margin: theme.spacing(2),
  },
  messageText: {
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  emailHelpButton: {
    fontSize: 12,
  },
});

function Message(props) {
  const {
    status,
    error,
    classes,
    token,
  } = props;
  let errorMessage = 'Register failed.. please try again';
  if (error) { // redundant, shouldn't get to this because button is disabled
    if (error.status === 400) {
      if (error.data.email) {
        errorMessage = 'Email already in use, please try another';
      }
      if (error.data.username) {
        errorMessage = 'Username already in use, please try another';
      }
    }
  }
  if (token) {
    return (
      <React.Fragment>
        <Typography className={classes.messageText} gutterBottom variant="subtitle1">You are currently logged in.</Typography>
        <Button href="/logout" variant="outlined" color="primary">Logout</Button>
      </React.Fragment>
    );
  }
  if (status === 'error') {
    return (
      <div className={classes.messageContainer}>
        <img src={errorIcon} alt="error" className={classes.icon} />
        <Typography
          className={classes.message}
          variant="subtitle1"
        >
          {errorMessage}
        </Typography>
      </div>
    );
  }
  if (status === 'success') {
    return (
      <React.Fragment>
        <img src={successIcon} alt="error" className={classes.icon} />
        <Typography className={classes.messageText} gutterBottom variant="subtitle1">Register Success!</Typography>
        <Typography className={classes.messageText} variant="body1">An email has been sent to your address. Please click the link within to activate your account.</Typography>
        <Typography className={classes.messageText} variant="body1">Login to get started.</Typography>
        <Button href="/login" variant="contained" color="secondary">Login</Button>
      </React.Fragment>
    );
  }
  return null;
}

class RegisterForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      firstname: '',
      lastname: '',
      email: '',
      emailIsValidating: false,
      username: '',
      usernameIsValidating: false,
      password: '',
      showPassword: false,
      errors: {},
      errorStates: {
        firstname: false,
        lastname: false,
        email: false,
        username: false,
        password: false,
      },
      status: 'pre',
      error: {},
      recaptcha: false,
    };

    this.handleFirstnameChange = this.handleFirstnameChange.bind(this);
    this.handleLastnameChange = this.handleLastnameChange.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleEmailBlur = this.handleEmailBlur.bind(this);
    this.handleUsernameChange = this.handleUsernameChange.bind(this);
    this.handleUsernameBlur = this.handleUsernameBlur.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.buttonState = this.buttonState.bind(this);
    this.handlePasswordToggle = this.handlePasswordToggle.bind(this);
    this.loadingSpinnerDisplay = this.loadingSpinnerDisplay.bind(this);
    this.onRecaptchaChange = this.onRecaptchaChange.bind(this);
    this.onRecaptchaError = this.onRecaptchaError.bind(this);
  }

  onRecaptchaChange(value) {
    console.log('Captcha value: ', value);
    if (value == null) {
      this.setState({ recaptcha: false });
    } else {
      this.setState({ recaptcha: true });
    }
  }

  onRecaptchaError(e) {
    this.setState({ recaptcha: false });
  }

  handleFirstnameChange(e) {
    e.preventDefault();
    const { errors, errorStates } = this.state;
    [errors.firstname, errorStates.firstname] = validateFirstname(e.target.value);
    this.setState({ firstname: e.target.value, errors, errorStates });
  }

  handleLastnameChange(e) {
    e.preventDefault();
    const { errors, errorStates } = this.state;
    [errors.lastname, errorStates.lastname] = validateLastname(e.target.value);
    this.setState({ lastname: e.target.value, errors, errorStates });
  }

  handleEmailChange(e) {
    e.preventDefault();
    const { errors, errorStates } = this.state;
    [errors.email, errorStates.email] = validateEmail(e.target.value);
    this.setState({ email: e.target.value, errors, errorStates });
  }

  handleEmailBlur(e) {
    e.preventDefault();
    // AJAX check if email is in use
    const { errors, errorStates, email } = this.state;
    this.setState({ emailIsValidating: true });

    getUserByEmail(e.target.value)
      .then((response) => {
        // user with username found, display error
        if (response.status === 200) {
          errors.email = EMAIL_ERROR_TEXT;
          errorStates.email = true;
          this.setState({ errors, errorStates, emailIsValidating: false });
        }
      }).catch((error) => {
        // user with username not found, good to go
        if (error.response) {
          if (error.response.status === 404) {
            delete errors.email;
            errorStates.email = false;
            [errors.email, errorStates.email] = validateEmail(email);
            this.setState({ errors, errorStates, emailIsValidating: false });
          }
        } else {
          errors.email = 'Something went wrong..';
          this.setState({ errors, emailIsValidating: false });
        }
      });
  }

  handleUsernameChange(e) {
    e.preventDefault();
    const { errors, errorStates } = this.state;
    [errors.username, errorStates.username] = validateUsername(e.target.value);
    this.setState({ username: e.target.value, errors, errorStates });
  }

  handleUsernameBlur(e) {
    e.preventDefault();
    // AJAX check if username is in use
    const { errors, errorStates, username } = this.state;
    this.setState({ usernameIsValidating: true });

    getUserByUsername(e.target.value)
      .then((response) => {
        // user with username found, display error
        if (response.status === 200) {
          errors.username = 'Username already in use';
          errorStates.username = true;
          this.setState({ errors, errorStates, usernameIsValidating: false });
        }
        // other responses?
      }).catch((error) => {
        // user with username not found, good to go
        if (error.response) {
          if (error.response.status === 404) {
            delete errors.username;
            errorStates.username = false;
            [errors.username, errorStates.username] = validateUsername(username);
            this.setState({ errors, errorStates, usernameIsValidating: false });
          }
        } else {
          errors.username = 'Something went wrong..';
          this.setState({ errors, usernameIsValidating: false });
        }
      });
  }

  handlePasswordChange(e) {
    e.preventDefault();
    const { errors, errorStates } = this.state;
    [errors.password, errorStates.password] = validatePassword(e.target.value);
    this.setState({ password: e.target.value, errors, errorStates });
  }

  handlePasswordToggle() {
    const { showPassword } = this.state;
    this.setState({ showPassword: !showPassword });
  }

  handleSubmit(e) {
    const {
      firstname,
      lastname,
      email,
      username,
      password,
    } = this.state;
    e.preventDefault();
    // form passes client side validation, do stuff
    this.setState({
      status: 'loading',
    });
    const user = {
      first_name: firstname,
      last_name: lastname,
      email,
      username,
      password,
    };
    registerUser(user)
      .then((response) => {
        // register success, show success with login button
        this.setState({
          status: 'success',
        });
      }).catch((error) => {
        // register failure, show form with errors
        // dispatch(failure(error.response));
        console.log(error.response);
        this.setState({
          error: error.response,
          status: 'error',
        });
      });
  }

  buttonState() {
    const {
      errorStates,
      firstname,
      lastname,
      email,
      username,
      password,
      recaptcha,
    } = this.state;
    let disabled = false;
    Object.values(errorStates).forEach((val) => {
      if (val !== false) {
        disabled = true;
      }
    });
    if ((firstname === '') || (lastname === '') || (email === '') || (username === '') || (password === '')) {
      disabled = true;
    }
    if (!recaptcha) {
      disabled = true;
    }
    return disabled;
  }

  loadingSpinnerDisplay() {
    const { classes } = this.props;
    const { status } = this.state;
    if (status === 'loading') {
      return (
        <LinearProgress color="secondary" variant="indeterminate" className={classes.progress} />
      );
    }
    return null;
  }

  render() {
    const { classes, token } = this.props;
    const {
      emailIsValidating,
      usernameIsValidating,
      status,
      error,
      errors,
      errorStates,
      firstname,
      lastname,
      email,
      username,
      password,
      showPassword,
    } = this.state;

    return (
      <Paper className={classes.paper}>

        <img src={logo} alt="Nia" className={classes.logo} />

        <Message
          classes={classes}
          status={status}
          error={error}
          token={token}
        />

        {status === 'success' || token != null
          ? null
          : (
            <form onSubmit={this.handleSubmit}>
              <FormGroup row>
                {/* FIRSTNAME */}
                <FormControl className={classes.formControl} error={errorStates.firstname}>
                  <InputLabel>First Name</InputLabel>
                  <Input value={firstname} onChange={this.handleFirstnameChange} />
                  <FormHelperText id="firstname-error-text">
                    {errors.firstname}
                  </FormHelperText>
                </FormControl>

                {/* LASTNAME */}
                <FormControl className={classes.formControl} error={errorStates.lastname}>
                  <InputLabel>Last Name</InputLabel>
                  <Input value={lastname} onChange={this.handleLastnameChange} />
                  <FormHelperText id="lastname-error-text">
                    {errors.lastname}
                  </FormHelperText>
                </FormControl>
              </FormGroup>

              {/* EMAIL */}
              <FormGroup row>
                <FormControl className={classes.formControl} error={errorStates.email}>
                  <InputLabel>Email</InputLabel>
                  <Input
                    value={email}
                    onChange={this.handleEmailChange}
                    onBlur={this.handleEmailBlur}
                  />
                  {emailIsValidating
                    ? <LinearProgress color="secondary" variant="indeterminate" />
                    : null
                  }
                  <FormHelperText id="email-error-text">
                    {errors.email}
                  </FormHelperText>
                </FormControl>
              </FormGroup>

              {errors.email === EMAIL_ERROR_TEXT && (
                <div className={classes.emailHelpButtonContainer}>
                  <Button className={classes.emailHelpButton} href="/login">Login</Button>
                  <Button className={classes.emailHelpButton} href="/account_recovery">Forgot Password</Button>
                </div>
              )}

              {/* USERNAME */}
              <FormGroup row>
                <FormControl className={classes.formControl} error={errorStates.username}>
                  <InputLabel>Username</InputLabel>
                  <Input
                    value={username}
                    onChange={this.handleUsernameChange}
                    onBlur={this.handleUsernameBlur}
                  />
                  {usernameIsValidating
                    ? <LinearProgress color="secondary" variant="indeterminate" />
                    : null
                  }
                  <FormHelperText id="username-error-text">
                    {errors.username}
                  </FormHelperText>
                </FormControl>
              </FormGroup>

              {/* PASSWORD */}
              <FormGroup row>
                <FormControl
                  className={classNames(classes.margin, classes.formControl)}
                  error={errorStates.password}
                >
                  <InputLabel htmlFor="adornment-password">Password</InputLabel>
                  <Input
                    id="adornment-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={this.handlePasswordChange}
                    endAdornment={(
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Toggle password visibility"
                          onClick={this.handlePasswordToggle}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )}
                  />
                  <FormHelperText id="password-error-text">
                    {errors.password}
                  </FormHelperText>
                </FormControl>
              </FormGroup>

              {/* RECAPTCHA */}
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITEKEY}
                onChange={this.onRecaptchaChange}
                onErrored={this.onRecaptchaError}
                className={classes.recaptcha}
              />

              {/* SUBMIT BUTTON */}
              <span>
                <Button
                  disabled={this.buttonState()}
                  type="submit"
                  variant="contained"
                  className={classes.button}
                  color="secondary"
                  fullWidth
                >
                  Register
                </Button>
                {this.loadingSpinnerDisplay()}
              </span>
            </form>
          )
        }
      </Paper>
    );
  }
}

Message.propTypes = {
  status: PropTypes.string,
  error: PropTypes.object,
  classes: PropTypes.object.isRequired,
  token: PropTypes.string,
};

RegisterForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  status: PropTypes.string,
  classes: PropTypes.object.isRequired,
  error: PropTypes.object,
  token: PropTypes.string,
};

function mapStateToProps(state) {
  const { token } = state.loginReducer;
  return {
    token,
  };
}

export default withStyles(styles)(connect(mapStateToProps)(RegisterForm));
